/**
 * Navbar Handlers
 *
 * Pure functions that apply commands to state.
 */

import { NavbarState } from './state';
import { NavbarCommand } from './commands';

type ExtractCommand<T extends string> = Extract<NavbarCommand, { type: T }>;

// =============================================================================
// DRAWER HANDLERS
// =============================================================================

/**
 * Open mobile drawer
 */
const handleDrawerOpen = ( state: NavbarState ): NavbarState => ({
  ...state,
  mobileDrawer: { isOpen: true }
});

/**
 * Close mobile drawer
 */
const handleDrawerClose = ( state: NavbarState ): NavbarState => ({
  ...state,
  mobileDrawer: { isOpen: false }
});

/**
 * Toggle mobile drawer
 */
const handleDrawerToggle = ( state: NavbarState ): NavbarState => ({
  ...state,
  mobileDrawer: { isOpen: !state.mobileDrawer.isOpen }
});

// =============================================================================
// MENU HANDLERS
// =============================================================================

/**
 * Open a menu
 */
const handleMenuOpen = (
  state: NavbarState,
  command: ExtractCommand<'navbar/menu/open'>
): NavbarState => {
  const newOpenMenus = new Set( state.openMenus );
  newOpenMenus.add( command.payload.menuKey );
  return {
    ...state,
    openMenus: newOpenMenus
  };
};

/**
 * Close a menu
 */
const handleMenuClose = (
  state: NavbarState,
  command: ExtractCommand<'navbar/menu/close'>
): NavbarState => {
  const newOpenMenus = new Set( state.openMenus );
  newOpenMenus.delete( command.payload.menuKey );
  return {
    ...state,
    openMenus: newOpenMenus
  };
};

/**
 * Lock a menu (prevent hover open after click)
 */
const handleMenuLock = (
  state: NavbarState,
  command: ExtractCommand<'navbar/menu/lock'>
): NavbarState => {
  const newLockedMenus = new Set( state.clickLockedMenus );
  newLockedMenus.add( command.payload.menuKey );
  return {
    ...state,
    clickLockedMenus: newLockedMenus
  };
};

/**
 * Unlock a menu
 */
const handleMenuUnlock = (
  state: NavbarState,
  command: ExtractCommand<'navbar/menu/unlock'>
): NavbarState => {
  const newLockedMenus = new Set( state.clickLockedMenus );
  newLockedMenus.delete( command.payload.menuKey );
  return {
    ...state,
    clickLockedMenus: newLockedMenus
  };
};

/**
 * Close all menus
 */
const handleMenuCloseAll = ( state: NavbarState ): NavbarState => ({
  ...state,
  openMenus: new Set(),
  clickLockedMenus: new Set()
});

// =============================================================================
// ACCOUNT HANDLERS
// =============================================================================

/**
 * Update account data from subscription
 */
const handleAccountUpdate = (
  state: NavbarState,
  command: ExtractCommand<'navbar/account/update'>
): NavbarState => ({
  ...state,
  account: {
    name: command.payload.name,
    data: command.payload.data
  }
});

// =============================================================================
// QR MODAL HANDLERS
// =============================================================================

/**
 * Open QR modal
 */
const handleQRModalOpen = ( state: NavbarState ): NavbarState => ({
  ...state,
  appLoginQRModal: {
    ...state.appLoginQRModal,
    isOpen: true
  }
});

/**
 * Close QR modal
 */
const handleQRModalClose = ( state: NavbarState ): NavbarState => ({
  ...state,
  appLoginQRModal: {
    isOpen: false,
    qrCodeImage: null,
    isLoading: false
  }
});

/**
 * Set QR loading state
 */
const handleQRModalSetLoading = (
  state: NavbarState,
  command: ExtractCommand<'navbar/qrModal/setLoading'>
): NavbarState => ({
  ...state,
  appLoginQRModal: {
    ...state.appLoginQRModal,
    isLoading: command.payload.isLoading
  }
});

/**
 * Set QR image
 */
const handleQRModalSetImage = (
  state: NavbarState,
  command: ExtractCommand<'navbar/qrModal/setImage'>
): NavbarState => ({
  ...state,
  appLoginQRModal: {
    ...state.appLoginQRModal,
    qrCodeImage: command.payload.image,
    isLoading: false
  }
});

// =============================================================================
// HANDLER MAP
// =============================================================================

/**
 * Handler map
 */
export const handlers = {
  // Drawer
  'navbar/drawer/open': handleDrawerOpen,
  'navbar/drawer/close': handleDrawerClose,
  'navbar/drawer/toggle': handleDrawerToggle,

  // Menu
  'navbar/menu/open': handleMenuOpen,
  'navbar/menu/close': handleMenuClose,
  'navbar/menu/lock': handleMenuLock,
  'navbar/menu/unlock': handleMenuUnlock,
  'navbar/menu/closeAll': handleMenuCloseAll,

  // Account
  'navbar/account/update': handleAccountUpdate,

  // QR Modal
  'navbar/qrModal/open': handleQRModalOpen,
  'navbar/qrModal/close': handleQRModalClose,
  'navbar/qrModal/setLoading': handleQRModalSetLoading,
  'navbar/qrModal/setImage': handleQRModalSetImage
};
